import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, RotateCcw, Loader2, Rocket, FileSpreadsheet, ShieldOff } from "lucide-react";

const FIELDS = ["name", "mobile", "email", "city", "pincode", "product", "source", "previous_status", "previous_remark", "lead_date"];

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return lines.slice(1).map((ln) => {
    const cells = ln.split(",");
    const row = {};
    headers.forEach((h, i) => { const k = FIELDS.includes(h) ? h : (h === "phone" || h === "phone_number" ? "mobile" : h); row[k] = (cells[i] || "").trim(); });
    return row;
  });
}

export default function ReactivationAI() {
  const [ov, setOv] = useState(null);
  const [batches, setBatches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [mode, setMode] = useState("create_if_no_match");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [camp, setCamp] = useState({ name: "", agent_id: "", import_batch_id: "", min_age_days: "" });

  const load = async () => {
    const [o, b, a] = await Promise.all([
      api.get("/ai/reactivation/overview"), api.get("/ai/reactivation/imports"), api.get("/ai/agents"),
    ]);
    setOv(o.data); setBatches(b.data.items); setAgents(a.data.items);
    if (a.data.items[0]) setCamp((c) => ({ ...c, agent_id: c.agent_id || a.data.items[0].id }));
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name);
    const text = await f.text();
    setRows(parseCSV(text));
    toast.success(`${parseCSV(text).length} rows detected`);
  };

  const runImport = async () => {
    if (!rows.length) return toast.error("Upload a CSV first");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/reactivation/import", { batch_name: fileName || "Old Leads", file_name: fileName, mode, rows });
      setResult(data); toast.success(`Imported ${data.imported}, updated ${data.updated}`);
      setCamp((c) => ({ ...c, import_batch_id: data.id }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const launch = async () => {
    if (!camp.agent_id) return toast.error("Select an AI agent");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/reactivation/campaign", { ...camp, min_age_days: camp.min_age_days || undefined, launch: true });
      toast.success(`Launched "${data.name}" — ${data.total_targeted} leads queued`); setCamp((c) => ({ ...c, name: "", min_age_days: "" })); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const KPI = ({ label, value, testid }) => (
    <div data-testid={testid} className="glass rounded-xl p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold font-heading mt-1">{value ?? 0}</p>
    </div>
  );

  return (
    <div className="space-y-6 pb-10" data-testid="reactivation-page">
      <div>
        <h1 className="text-2xl font-bold font-heading">AI Lead Reactivation</h1>
        <p className="text-sm text-muted-foreground">Re-engage old leads automatically with AI outbound calling.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Total Imported" value={ov?.total_imported} testid="kpi-imported" />
        <KPI label="Suppressed / DNC" value={ov?.suppressed_dnc} testid="kpi-dnc" />
        <KPI label="Campaigns" value={ov?.campaigns} testid="kpi-campaigns" />
        <KPI label="Queued" value={ov?.queued} testid="kpi-queued" />
        <KPI label="Recovered" value={ov?.recovered} testid="kpi-recovered" />
      </div>

      {/* Import */}
      <div className="glass rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 font-semibold"><Upload className="h-4 w-4 text-primary" /> Import Old Leads (CSV)</div>
        <p className="text-xs text-muted-foreground">Columns: name, mobile, email, city, pincode, product, source, previous_status, previous_remark. Duplicates are flagged, never blocked.</p>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label className="text-xs text-muted-foreground">CSV file</Label>
            <Input type="file" accept=".csv" onChange={onFile} data-testid="import-file" className="mt-1 bg-slate-900/60 border-slate-700" />
            {fileName && <p className="text-xs text-primary mt-1"><FileSpreadsheet className="h-3 w-3 inline mr-1" />{fileName} · {rows.length} rows</p>}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Import mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="import-mode" className="mt-1 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="create_if_no_match">Create only if no match</SelectItem>
                <SelectItem value="create">Always create new</SelectItem>
                <SelectItem value="update">Update matching leads</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={runImport} disabled={busy || !rows.length} data-testid="run-import" className="bg-primary text-primary-foreground">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Import {rows.length || ""} Leads</>}
        </Button>
        {result && (
          <div data-testid="import-result" className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
            {[["Imported", result.imported], ["Updated", result.updated], ["Duplicates", result.duplicates], ["Invalid", result.invalid], ["Total", result.total_rows]].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-slate-900/50 border border-slate-800 p-2 text-center"><p className="text-[11px] text-muted-foreground">{k}</p><p className="font-bold">{v}</p></div>
            ))}
          </div>
        )}
      </div>

      {/* Create campaign */}
      <div className="glass rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 font-semibold"><RotateCcw className="h-4 w-4 text-primary" /> Create Reactivation Campaign</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs text-muted-foreground">Campaign name</Label>
            <Input value={camp.name} onChange={(e) => setCamp({ ...camp, name: e.target.value })} data-testid="camp-name" placeholder="September Reactivation" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
          <div><Label className="text-xs text-muted-foreground">AI agent</Label>
            <Select value={camp.agent_id} onValueChange={(v) => setCamp({ ...camp, agent_id: v })}>
              <SelectTrigger data-testid="camp-agent" className="mt-1 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Select agent" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs text-muted-foreground">From import batch (optional)</Label>
            <Select value={camp.import_batch_id || "none"} onValueChange={(v) => setCamp({ ...camp, import_batch_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="camp-batch" className="mt-1 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Any old leads" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="none">Any old leads</SelectItem>
                {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.batch_name} ({b.imported})</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs text-muted-foreground">Min lead age (days, optional)</Label>
            <Input type="number" value={camp.min_age_days} onChange={(e) => setCamp({ ...camp, min_age_days: e.target.value })} data-testid="camp-age" placeholder="e.g. 60" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldOff className="h-3 w-3" /> DNC leads are automatically excluded and re-checked before each call.</p>
        <Button onClick={launch} disabled={busy} data-testid="launch-reactivation" className="bg-primary text-primary-foreground">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rocket className="h-4 w-4 mr-1.5" />Launch Campaign</>}
        </Button>
      </div>
    </div>
  );
}
